import { z } from 'zod';

export const MerchantTimeZoneSchema: z.ZodType<string> = z
    .string()
    .trim()
    .min(1)
    .refine((timeZone) => {
        try {
            new Intl.DateTimeFormat('en-US', { timeZone }).format(0);
            return true;
        } catch {
            return false;
        }
    }, 'Invalid IANA timezone');

export type MerchantTimeZone = z.infer<typeof MerchantTimeZoneSchema>;

export const ManualMerchantTimeZoneSchema: z.ZodType<MerchantTimeZone> =
    MerchantTimeZoneSchema.refine(
        (timeZone) => !/^[+-]/.test(timeZone),
        'Choose a named IANA timezone'
    );

export const SYSTEM_DISPLAY_TIME_ZONE = 'America/New_York' as const;
export type SystemDisplayTimeZone = typeof SYSTEM_DISPLAY_TIME_ZONE;
export const SystemDisplayTimeZoneSchema: z.ZodType<SystemDisplayTimeZone> =
    z.literal(SYSTEM_DISPLAY_TIME_ZONE);

const unresolvedTimeZoneMessage = 'Timezone unavailable';

type DateFormatterPartKey =
    | 'day'
    | 'dayPeriod'
    | 'hour'
    | 'minute'
    | 'month'
    | 'second'
    | 'timeZoneName'
    | 'year';

const buildDateFormatterParts = (
    value: string,
    timeZone: MerchantTimeZone,
    options: Intl.DateTimeFormatOptions
): Readonly<Record<DateFormatterPartKey, string>> | null => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone,
        ...options,
    });
    const parts = formatter.formatToParts(date);
    const partMap: Partial<Record<DateFormatterPartKey, string>> = {};

    for (const part of parts) {
        switch (part.type) {
            case 'day':
            case 'dayPeriod':
            case 'hour':
            case 'minute':
            case 'month':
            case 'second':
            case 'timeZoneName':
            case 'year':
                partMap[part.type] = part.value;
                break;
            default:
                break;
        }
    }

    return {
        day: partMap.day ?? '',
        dayPeriod: partMap.dayPeriod ?? '',
        hour: partMap.hour ?? '',
        minute: partMap.minute ?? '',
        month: partMap.month ?? '',
        second: partMap.second ?? '',
        timeZoneName: partMap.timeZoneName ?? '',
        year: partMap.year ?? '',
    };
};

const formatTimestamp = (
    value: string,
    timeZone: MerchantTimeZone | null,
    options: Intl.DateTimeFormatOptions,
    render: (parts: Readonly<Record<DateFormatterPartKey, string>>) => string
): string => {
    if (timeZone === null) {
        return unresolvedTimeZoneMessage;
    }
    const parts = buildDateFormatterParts(value, timeZone, options);
    return parts === null ? value : render(parts).trim();
};

export const formatTimestampForMerchantUi = (
    value: string,
    timeZone: MerchantTimeZone | null
): string =>
    formatTimestamp(
        value,
        timeZone,
        {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short',
        },
        (parts) =>
            `${parts.month} ${parts.day}, ${parts.year}, ${parts.hour}:${parts.minute} ${parts.dayPeriod} ${parts.timeZoneName}`
    );

export const formatTimestampForMerchantReceipt = (
    value: string,
    timeZone: MerchantTimeZone | null
): string =>
    formatTimestamp(
        value,
        timeZone,
        {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZoneName: 'short',
        },
        (parts) =>
            `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second} ${parts.timeZoneName}`
    );

export const formatTimestampAsMerchantDateForUi = (
    value: string,
    timeZone: MerchantTimeZone | null
): string =>
    formatTimestamp(
        value,
        timeZone,
        { year: 'numeric', month: 'short', day: '2-digit', timeZoneName: 'short' },
        (parts) => `${parts.month} ${parts.day}, ${parts.year} ${parts.timeZoneName}`
    );

export const formatTimestampAsMerchantTimeForUi = (
    value: string,
    timeZone: MerchantTimeZone | null
): string =>
    formatTimestamp(
        value,
        timeZone,
        { hour: '2-digit', minute: '2-digit', hour12: true, timeZoneName: 'short' },
        (parts) => `${parts.hour}:${parts.minute} ${parts.dayPeriod} ${parts.timeZoneName}`
    );
